import React, { useState } from 'react';
import { Button, Card, Table, Loader } from 'semantic-ui-react';
import { injectIntl } from 'react-intl';
import Pager from '@uniiverse/library/components/pagination';
import { useQuery } from '@apollo/react-hooks';
import { formatAmount } from 'lib/currencies';
import { ErrorPage } from 'components/utility/ErrorPage';
import locale from '../locale';
import userChargebacksQuery from './chargebacks.gql';

const Chargebacks = injectIntl(({ intl: { formatMessage }, intl, user, currency }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const paginationLimit = 5;

  const { data, loading, error, fetchMore } = useQuery(userChargebacksQuery, {
    variables: { id: user.id, currency, offset: 0, limit: paginationLimit },
  });

  if (loading) return <Loader active />;
  if (error) return <ErrorPage />;
  if (data.host.chargebacks.totalCount === 0) return null;

  const { nodes: chargebacks, totalCount } = data.host.chargebacks;
  const totalPages = Math.ceil(totalCount / paginationLimit);

  const changePage = page => {
    fetchMore({
      variables: { offset: page.selected * paginationLimit },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return { ...prev, ...fetchMoreResult };
      },
    });
    setCurrentPage(page.selected + 1);
  };

  return (
    <Card fluid>
      <Card.Content>
        <Card.Header>{formatMessage(locale.chargebacks)}</Card.Header>
        {formatMessage(locale.whyChargeback)}{' '}
        <a href="https://www.universe.com/help/articles/360002486172-What-is-a-chargeback-Universe-Payments-">
          {formatMessage(locale.learnMore)}
        </a>
        <Table basic singleLine>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{formatMessage(locale.date)}</Table.HeaderCell>
              <Table.HeaderCell>{formatMessage(locale.eventName)}</Table.HeaderCell>
              <Table.HeaderCell>{formatMessage(locale.buyerName)}</Table.HeaderCell>
              <Table.HeaderCell>{formatMessage(locale.netSales)}</Table.HeaderCell>
              <Table.HeaderCell>{formatMessage(locale.disputed)}</Table.HeaderCell>
              <Table.HeaderCell>{formatMessage(locale.fee)}</Table.HeaderCell>
              <Table.HeaderCell>{formatMessage(locale.status)}</Table.HeaderCell>
              <Table.HeaderCell collapsing></Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          {chargebacks && (
            <Table.Body>
              {chargebacks.map(cb => {
                return (
                  <Table.Row positive={cb.state === 'won'} negative={cb.state === 'lost'}>
                    <Table.Cell>
                      {new Date(cb.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </Table.Cell>
                    <Table.Cell>
                      <a href={`/events/${cb.order.event.title.replace(' ', '-')}-tickets-${cb.order.event.slug}`}>
                        {cb.order.event.title}
                      </a>
                    </Table.Cell>
                    <Table.Cell>{cb.buyer.name}</Table.Cell>
                    <Table.Cell>{formatAmount(intl, cb.order.costBreakdown.payment, currency, false)}</Table.Cell>
                    <Table.Cell>{formatAmount(intl, cb.amount, currency, false)}</Table.Cell>
                    <Table.Cell>{formatAmount(intl, cb.fee, currency, false)}</Table.Cell>
                    <Table.Cell>{cb.state}</Table.Cell>
                    <Table.Cell textAlign="right">
                      <Button href={`/dashboard/conversations/withUser/${cb.buyer.id}`}>
                        {formatMessage(locale.message)}
                      </Button>
                      <Button href={`/dashboard/order/${cb.order.id}`}>{formatMessage(locale.view)}</Button>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          )}
        </Table>
        <Pager currentPage={currentPage} pageCount={totalPages} onPageChange={selected => changePage(selected)} />
      </Card.Content>
    </Card>
  );
});

export default Chargebacks;
